import React, { useState, useEffect } from 'react';
import { UseFormRegister, FieldErrors, UseFormTrigger } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface MileageReportFormProps {
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  trigger: UseFormTrigger<any>;
  isTaxFree: string;
  selectedFiles: File[];
  total: string;
  fileError?: string;
  onFormChange: (field: string, value: any) => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  formData?: any;
  vatFromPreferences?: number;
}

const MileageReportForm: React.FC<MileageReportFormProps> = ({
  register,
  errors,
  trigger,
  isTaxFree,
  selectedFiles,
  total,
  fileError,
  onFormChange,
  onFileUpload,
  onRemoveFile,
  formData,
  vatFromPreferences
}) => {
  const { t } = useTranslation();

  // Dropdown states
  const [isTaxFreeDropdownOpen, setIsTaxFreeDropdownOpen] = useState(false);
  const [showOnInvoiceDropdownOpen, setShowOnInvoiceDropdownOpen] = useState(false);
  const [isPdfAttachedDropdownOpen, setIsPdfAttachedDropdownOpen] = useState(false);

  // Dropdown refs
  const isTaxFreeDropdownRef = React.useRef<HTMLDivElement>(null);
  const showOnInvoiceDropdownRef = React.useRef<HTMLDivElement>(null);
  const isPdfAttachedDropdownRef = React.useRef<HTMLDivElement>(null);

  // Options
  const taxFreeOptions = [
    { value: '', label: t('SELECT_OPTION') },
    { value: 'yes', label: t('YES') },
    { value: 'no', label: t('NO') }
  ];

  const yesNoOptions = [
    { value: 'yes', label: t('YES') },
    { value: 'no', label: t('NO') }
  ];

  // Get label helpers
  const getTaxFreeLabel = (value: string) => {
    const option = taxFreeOptions.find(opt => opt.value === value);
    return option?.label || t('SELECT_OPTION');
  };

  const getYesNoLabel = (value: string) => {
    const option = yesNoOptions.find(opt => opt.value === value);
    return option?.label || t('YES');
  };

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isTaxFreeDropdownRef.current && !isTaxFreeDropdownRef.current.contains(event.target as Node)) {
        setIsTaxFreeDropdownOpen(false);
      }
      if (showOnInvoiceDropdownRef.current && !showOnInvoiceDropdownRef.current.contains(event.target as Node)) {
        setShowOnInvoiceDropdownOpen(false);
      }
      if (isPdfAttachedDropdownRef.current && !isPdfAttachedDropdownRef.current.contains(event.target as Node)) {
        setIsPdfAttachedDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      {/* Row 1: Is Tax Free & Show on Invoice */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('IS_MILEAGE_TAX_FREE')}</label>
          <div className="custom-dropdown" ref={isTaxFreeDropdownRef} style={{ position: 'relative' }}>
            <div
              className={`dropdown-trigger ${isTaxFreeDropdownOpen ? 'open' : ''}`}
              onClick={() => setIsTaxFreeDropdownOpen(!isTaxFreeDropdownOpen)}
              style={{
                position: 'relative',
                height: '46px',
                border: `1px solid ${errors.isTaxFree ? '#dc3545' : (isTaxFreeDropdownOpen ? '#6750b8' : '#eaebf0')}`,
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: isTaxFreeDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none'
              }}
            >
              <span style={{
                flex: 1,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '15px',
                fontWeight: 500,
                lineHeight: '22px',
                color: formData?.isTaxFree ? '#252525' : '#68727d'
              }}>
                {getTaxFreeLabel(formData?.isTaxFree || '')}
              </span>
              <svg
                className={`dropdown-arrow ${isTaxFreeDropdownOpen ? 'rotated' : ''}`}
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  flexShrink: 0,
                  color: '#68727d',
                  transition: 'transform 0.2s ease',
                  transform: isTaxFreeDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {isTaxFreeDropdownOpen && (
              <div
                className="dropdown-list"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #eaebf0',
                  borderRadius: '6px',
                  padding: '4px',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000
                }}
              >
                <div className="dropdown-content">
                  {taxFreeOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`list-item ${formData?.isTaxFree === option.value ? 'selected' : ''}`}
                      onClick={() => {
                        onFormChange('isTaxFree', option.value);
                        trigger('isTaxFree');
                        setIsTaxFreeDropdownOpen(false);
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s ease',
                        backgroundColor: formData?.isTaxFree === option.value ? '#f4f3f9' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f4f3f9'}
                      onMouseLeave={(e) => {
                        if (formData?.isTaxFree !== option.value) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div className="text-input">
                        <div className="paragraph-container">
                          <p className="option-text" style={{ margin: 0, fontSize: '15px', color: '#252525' }}>
                            {option.label}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {errors.isTaxFree && (
            <div className="error-message">{errors.isTaxFree.message?.toString()}</div>
          )}
        </div>

        {/* Show on Invoice - Only if isTaxFree is NOT 'no' */}
        {isTaxFree !== 'no' && (
          <div className="form-group">
            <label>{t('SHOW_MILEAGE_ON_INVOICE')}</label>
            <div className="custom-dropdown" ref={showOnInvoiceDropdownRef} style={{ position: 'relative' }}>
              <div
                className={`dropdown-trigger ${showOnInvoiceDropdownOpen ? 'open' : ''}`}
                onClick={() => setShowOnInvoiceDropdownOpen(!showOnInvoiceDropdownOpen)}
                style={{
                  position: 'relative',
                  height: '46px',
                  border: `1px solid ${showOnInvoiceDropdownOpen ? '#6750b8' : '#eaebf0'}`,
                  borderRadius: '6px',
                  backgroundColor: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: showOnInvoiceDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none'
                }}
              >
                <span style={{
                  flex: 1,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: '15px',
                  fontWeight: 500,
                  lineHeight: '22px',
                  color: '#252525'
                }}>
                  {getYesNoLabel(formData?.showOnInvoice || 'yes')}
                </span>
                <svg
                  className={`dropdown-arrow ${showOnInvoiceDropdownOpen ? 'rotated' : ''}`}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  style={{
                    flexShrink: 0,
                    color: '#68727d',
                    transition: 'transform 0.2s ease',
                    transform: showOnInvoiceDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {showOnInvoiceDropdownOpen && (
                <div
                  className="dropdown-list"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    width: '100%',
                    backgroundColor: '#ffffff',
                    border: '1px solid #eaebf0',
                    borderRadius: '6px',
                    padding: '4px',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000
                  }}
                >
                  <div className="dropdown-content">
                    {yesNoOptions.map((option) => (
                      <div
                        key={option.value}
                        className={`list-item ${(formData?.showOnInvoice || 'yes') === option.value ? 'selected' : ''}`}
                        onClick={() => {
                          onFormChange('showOnInvoice', option.value);
                          trigger('showOnInvoice');
                          setShowOnInvoiceDropdownOpen(false);
                        }}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          transition: 'background-color 0.2s ease',
                          backgroundColor: (formData?.showOnInvoice || 'yes') === option.value ? '#f4f3f9' : 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f4f3f9'}
                        onMouseLeave={(e) => {
                          if ((formData?.showOnInvoice || 'yes') !== option.value) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <div className="text-input">
                          <div className="paragraph-container">
                            <p className="option-text" style={{ margin: 0, fontSize: '15px', color: '#252525' }}>
                              {option.label}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Upload File (PDF only) - Full Width */}
      <div className="form-group">
        <label>{t('UPLOAD_FILE_PDF_1MB')}</label>
        <div className="file-upload-wrapper">
          <div 
            className="file-upload-area"
            onClick={() => document.getElementById('file-upload-pdf')?.click()}
            style={{ cursor: 'pointer' }}
          >
            <button 
              type="button" 
              className="browse-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {selectedFiles.length > 0 ? t('CHANGE_PDF_FILE') : t('UPLOAD_PDF_FILE')}
            </button>
            <input 
              type="file" 
              id="file-upload-pdf" 
              accept=".pdf,application/pdf" 
              onChange={onFileUpload}
              onClick={(e) => { (e.target as HTMLInputElement).value = ''; }}
              className="file-input"
              style={{ display: 'none' }}
            />
          </div>
          {selectedFiles.length > 0 && (
            <div className="uploaded-files">
              {selectedFiles.map((file, index) => (
                <div key={index} className="file-tag">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <a
                    href={URL.createObjectURL(file)}
                    download={file.name}
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: '#6750b8',
                    }}
                  >
                    {file.name}
                  </a>
                  <span style={{ marginLeft: '8px', color: '#888', fontSize: '12px' }}>
                    ({(file.size / 1024).toFixed(2)} {t('KB')})
                  </span>
                  <button type="button" className="remove-btn" onClick={() => onRemoveFile(index)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        {fileError && (
          <div className="error-message" style={{ marginTop: '8px' }}>{fileError}</div>
        )}
      </div>

      {/* Show on Invoice - Only if file is uploaded */}
      {selectedFiles.length > 0 && (
        <div className="form-group">
          <label>{t('SHOW_PDF_TO_CUSTOMER')}</label>
          <div className="custom-dropdown" ref={isPdfAttachedDropdownRef} style={{ position: 'relative' }}>
            <div
              className={`dropdown-trigger ${isPdfAttachedDropdownOpen ? 'open' : ''}`}
              onClick={() => setIsPdfAttachedDropdownOpen(!isPdfAttachedDropdownOpen)}
              style={{
                position: 'relative',
                height: '46px',
                border: `1px solid ${isPdfAttachedDropdownOpen ? '#6750b8' : '#eaebf0'}`,
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: isPdfAttachedDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none'
              }}
            >
              <span style={{
                flex: 1,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '15px',
                fontWeight: 500,
                lineHeight: '22px',
                color: '#252525'
              }}>
                {getYesNoLabel(formData?.isPdfAttachedToInvoice || 'yes')}
              </span>
              <svg
                className={`dropdown-arrow ${isPdfAttachedDropdownOpen ? 'rotated' : ''}`}
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  flexShrink: 0,
                  color: '#68727d',
                  transition: 'transform 0.2s ease',
                  transform: isPdfAttachedDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {isPdfAttachedDropdownOpen && (
              <div
                className="dropdown-list"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  left: 0,
                  width: '100%',
                  backgroundColor: '#ffffff',
                  border: '1px solid #eaebf0',
                  borderRadius: '6px',
                  padding: '4px',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000
                }}
              >
                <div className="dropdown-content">
                  {yesNoOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`list-item ${(formData?.isPdfAttachedToInvoice || 'yes') === option.value ? 'selected' : ''}`}
                      onClick={() => {
                        onFormChange('isPdfAttachedToInvoice', option.value);
                        trigger('isPdfAttachedToInvoice');
                        setIsPdfAttachedDropdownOpen(false);
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background-color 0.2s ease',
                        backgroundColor: (formData?.isPdfAttachedToInvoice || 'yes') === option.value ? '#f4f3f9' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f4f3f9'}
                      onMouseLeave={(e) => {
                        if ((formData?.isPdfAttachedToInvoice || 'yes') !== option.value) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <div className="text-input">
                        <div className="paragraph-container">
                          <p className="option-text" style={{ margin: 0, fontSize: '15px', color: '#252525' }}>
                            {option.label}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Row 2: Travel Date & Distance */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('TRAVEL_DATE')}</label>
          <input
            type="date"
            {...register('travelDate', { required: t('PLEASE_SELECT_TRAVEL_DATE') })}
            className={`form-control ${errors.travelDate ? 'error' : ''}`}
            onChange={(e) => {
              onFormChange('travelDate', e.target.value);
              trigger('travelDate');
            }}
          />
          {errors.travelDate && (
            <div className="error-message">{errors.travelDate.message?.toString()}</div>
          )}
        </div>

        <div className="form-group">
          <label>{t('DISTANCE_KM')}</label>
          <input
            type="number"
            min="0"
            {...register('distance', { required: t('PLEASE_ENTER_DISTANCE') })}
            className={`form-control ${errors.distance ? 'error' : ''}`}
            placeholder={t('ENTER_DISTANCE')}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || parseFloat(value) >= 0) {
                onFormChange('distance', value);
                trigger('distance');
              }
            }}
          />
          {errors.distance && (
            <div className="error-message">{errors.distance.message?.toString()}</div>
          )}
        </div>
      </div>

      {/* Row 3: Reason for Trip & Car Registration */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('REASON_FOR_TRIP')}</label>
          <input
            type="text"
            {...register('reason', { required: t('PLEASE_ENTER_REASON') })}
            className={`form-control ${errors.reason ? 'error' : ''}`}
            placeholder={t('WRITE_HERE')}
            onChange={(e) => {
              onFormChange('reason', e.target.value);
              trigger('reason');
            }}
          />
          {errors.reason && (
            <div className="error-message">{errors.reason.message?.toString()}</div>
          )}
        </div>

        <div className="form-group">
          <label>{t('CAR_REGISTRATION')}</label>
          <input
            type="text"
            {...register('carRegistration', { required: t('PLEASE_ENTER_CAR_REG') })}
            className={`form-control ${errors.carRegistration ? 'error' : ''}`}
            placeholder={t('CAR_REG_PLACEHOLDER')}
            onChange={(e) => {
              onFormChange('carRegistration', e.target.value);
              trigger('carRegistration');
            }}
          />
          {errors.carRegistration && (
            <div className="error-message">{errors.carRegistration.message?.toString()}</div>
          )}
        </div>
      </div>

      {/* Row 4: Trip Started Place (Full Width) */}
      <div className="form-group">
        <label>{t('TRIP_STARTED_PLACE')}</label>
        <input
          type="text"
          {...register('tripStartPlace', { required: t('PLEASE_ENTER_START_PLACE') })}
          className={`form-control ${errors.tripStartPlace ? 'error' : ''}`}
          placeholder={t('WRITE_HERE')}
          onChange={(e) => {
            onFormChange('tripStartPlace', e.target.value);
            trigger('tripStartPlace');
          }}
        />
        {errors.tripStartPlace && (
          <div className="error-message">{errors.tripStartPlace.message?.toString()}</div>
        )}
      </div>

      {/* Summary Section */}
      <div className="summary-section">
        <div className="summary-item">
          <span className="summary-label">{t('ALLOWANCE_PER_KM')}</span>
          <span className="summary-value">{t('TOTAL')}: {total} {t('KR')}</span>
        </div>
      </div>
    </>
  );
};

export default MileageReportForm;
